import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Attribute, ListTruncation, } from '../../models';
import { FilterCollapseStateManager } from '../../classes';
import { ResetButtonStateUtil } from '../../utils/reset-button-state.util';
import { AttributeOption, RangeFilters } from '~shared/models';
import { ScrollUtils } from '~shared/utils/scroll.utils';
import { MatDialog } from '@angular/material/dialog';
import { AttributeModalComponent } from '~app/modules/filters/components/attribute-modal/attribute-modal.component';
import { FILTER_ATTRIBUTE_KEYS } from '~app/modules/filters/constants';
import {
  AttributeModalNewDesignComponent
} from '~app/modules/filters/components/attribute-modal-new-design/attribute-modal-new-design.component';
import { getRouterState, RouterStateUrl } from '~core/store';
import { select, Store } from '@ngrx/store';
import { RouterReducerState } from '@ngrx/router-store';

@Component({
  selector: 'app-search-attributes-list',
  templateUrl: './attributes-list.component.html',
  styleUrls: ['./attributes-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AttributesListComponent implements OnChanges, OnDestroy {
  @Input() attributes: Attribute[];
  @Input() isMobile: boolean;
  @Input() isTablet: boolean;
  @Input() isDesktop: boolean;
  @Input() isBrandPLPPage: boolean;
  @Input() queryParams: { [key: string]: any };
  @Input() attributeFilters: { key: string, value: string | number[] }[];
  @Input() isMobileOrTablet: boolean;
  @Output() attributeFilterChanged = new EventEmitter<any>();
  @Output() rangeFilterChanged = new EventEmitter<RangeFilters>();
  @Output() resetAttribute = new EventEmitter<string>();
  @Input() isSpecificAttribute: boolean;
  @Input() limitOption: ListTruncation;
  @Input() maxAttrOptions: ListTruncation;

  _attributes: Attribute[] = [];
  attributeFacetsForm: FormGroup;
  attributeExpandMap: { [key: string]: number } = {};
  routerStateParams;
  private unsubscribe$ = new Subject();

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private store: Store<RouterReducerState>) {
    this.store.pipe(
      select(getRouterState),
      takeUntil(this.unsubscribe$)
    ).subscribe(({queryParams}: RouterStateUrl) => {
      this.routerStateParams = queryParams;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.attributes) {
      this.updateAttrExpandMap();
      this.updateAttributesFilters();
    }

    this.attributeFacetsForm = this.buildAttributesForm();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  getAttributes(): Attribute[] {
    return this._attributes.filter(attribute => attribute.id !== FILTER_ATTRIBUTE_KEYS.BRAND);
  }

  getExtendedState(attributeOption: AttributeOption): boolean {
    if (!this.queryParams || !this.queryParams.filter) {
      return false;
    }

    const {filter: {options = {}}} = this.queryParams || {};
    const activeAttributeKey: string = Object.keys(options)
      .find(option => attributeOption.id.startsWith(option));

    if (activeAttributeKey) {
      const isSelected = options[activeAttributeKey]
        .some((attributeOptionValue: string) => attributeOptionValue.toString() === attributeOption.name);

      return !isSelected;
    }

    return false;
  }

  onLayerSelected(attribute: Attribute, attributeOption: AttributeOption): void {
    const selectedAttrs = Object.entries(this.attributeFacetsForm.value)
      .filter(([_, value]) => value)
      .reduce((acc, [key, _value]) => {
        const [attrType, attrValue] = key.split('--');
        const nextIndex = Object.keys(acc[`${attrType}`] || {}).length;

        return {
          ...acc,
          [`${attrType}`]: {...acc[`${attrType}`], [`${nextIndex}`]: attrValue},
        };
      }, {});

    // This may cause closing the expanded attribute section
    // before setting the selected attribute to the top
    this.closeExpandedAttribute(attribute?.id);

    this.scrollToSelectedElement(attribute, attributeOption);
    this.attributeFilterChanged.emit({selectedAttrs, content: attributeOption?.value, attributeId: attribute?.id});
  }

  private getAttributeOptionsFields(attribute: Attribute) {
    return attribute?.options.reduce((acc, attrOption) => ({
      ...acc,
      [`${attribute.id}--${attrOption.value}`]: this.getAttributeValueFromUrl(attribute.id, attrOption.value),
    }), {});
  }

  openAttributeOptionsModal(attribute: Attribute) {
    let width: string = '100%';

    if (this.isTablet) {
      width = '60%';
    }

    if (this.isDesktop) {
      width = '40%';
    }

    const options = {
      width,
      maxWidth: '690px',
      backdropClass: 'attr-options-modal',
      panelClass: 'attr-options-modal__container'
    };

    let dialogRef;

    const filterFields = this.getAttributeOptionsFields(attribute);
    const dataObject = {
      attribute,
      isMobileOrTablet: this.isMobileOrTablet,
      filterFields,
      queryParams: this.routerStateParams,
      isSpecificAttribute: this.isSpecificAttribute
    };
    const configObject = {
      ...options,
      data: dataObject
    };

    if (attribute?.id === 'brand' || attribute?.id === 'seller') {
      dialogRef = this.dialog.open(AttributeModalNewDesignComponent, configObject);
    } else {
      dialogRef = this.dialog.open(AttributeModalComponent, configObject);
    }


    dialogRef.afterClosed()
      .pipe(
        takeUntil(this.unsubscribe$),
        // @ts-ignore
        filter(dialogResult => dialogResult)
      )
      .subscribe(({attribute, formValue}) => {
        if (formValue) {
          this.attributeFacetsForm = this.fb.group({
            ...this.attributeFacetsForm.value,
            ...formValue
          });
          this.attributeFacetsForm.patchValue(formValue);
          this.onLayerSelected(attribute, undefined);
        }
      });
  }

  toggleExpand(attrId: string, attrOptionLength: number): void {
    const currentAttr = this.attributes.find(attr => attr.id === attrId);

    if (attrOptionLength > this.maxAttrOptions) {
      this.openAttributeOptionsModal(currentAttr);
    } else {
      const attrOptionsCount: number = currentAttr?.options?.length || 0;
      this.attributeExpandMap[attrId] = this.attributeExpandMap[attrId] > this.limitOption
        ? this.limitOption
        : attrOptionsCount;
    }
  }

  closeExpandedAttribute(attrId: string) {
    this.attributeExpandMap[attrId] = this.limitOption;
  }

  reset(attribute: Attribute): void {
    this.resetAttribute.emit(attribute.id);
  }

  updateFilterCollapseState(key: string, expanded: boolean): void {
    if (!this.isSpecificAttribute) {
      FilterCollapseStateManager.instance.update(key, expanded);
    }
  }

  isFilterCollapseExpanded(key: string): boolean {
    return FilterCollapseStateManager.instance.isExpanded(key);
  }

  isResetButtonShown(key: string): boolean {
    return !ResetButtonStateUtil.isHidden(key, this.queryParams);
  }

  filterByRange(ranges: RangeFilters): void {
    this.rangeFilterChanged.emit(ranges);
  }

  isAttributeOptionSelected(attributeId: string, optionValue: any): boolean {
    return !!this.attributeFacetsForm.value[`${attributeId}--${optionValue}`];
  }

  private buildAttributesForm(): FormGroup {
    const flattenAttributes: {} = this._attributes.reduce((acc, attr) => {
      const attributeOptionsFields = attr?.options.reduce((acc, attrOption: AttributeOption) => {
        if (attrOption?.id === FILTER_ATTRIBUTE_KEYS.REBUY_GUARANTEE_OPTION) {
          return {
            ...acc
          };
        }
        return {
          ...acc,
          [`${attr.id}--${attrOption.value}`]: this.getAttributeValueFromUrl(attr.id, attrOption.value),
        };
      }, {});

      return {
        ...acc,
        ...attributeOptionsFields,
      };
    }, {});

    return this.fb.group({...flattenAttributes});
  }

  private getAttributeValueFromUrl(attr: string, attrValue: string): boolean {
    const {
      filter: {
        options = {},
      } = {},
    } = this.queryParams || {};

    return options[`${attr}`]
      ? Object.values(options[`${attr}`]).some(v => v.toString() === attrValue)
      : false;
  }

  private updateAttrExpandMap(): void {
    this.attributeExpandMap = this.attributes
      .reduce((expandMap, attr: Attribute) => {
        const optionsCount: number = attr?.options?.length || 0;
        const prevLimit: number = this.attributeExpandMap && this.attributeExpandMap[attr.id];

        const limitOption: number = optionsCount > this.limitOption
          ? this.limitOption
          : optionsCount;

        return {
          ...expandMap,
          [`${attr.id}`]: prevLimit || limitOption,
        };
      }, {});
  }

  private scrollToSelectedElement(attribute: Attribute, attributeOption: AttributeOption): void {
    if (this.isMobileOrTablet && !!attributeOption) {
      const id: string = `${attribute?.id}--${attributeOption?.value}`;
      ScrollUtils.scrollToElementById(id);
    }
  }

  private updateAttributesFilters() {
    this._attributes = this.attributes.map(attribute => Object.assign({}, attribute, {
      options: this.reorderAttributeOptions(attribute.id, attribute?.options || [])
    }));
  }

  private reorderAttributeOptions(name: string, options: AttributeOption[]): AttributeOption[] {
    const attributesSelectedOptionsValues = this.attributeFilters[name]?.map(i => i.toString());
    if (!attributesSelectedOptionsValues) {
      return options;
    }

    return [...options].sort((a, b) => {
      const isOptionASelected = attributesSelectedOptionsValues.includes(a.value);
      const isOptionBSelected = attributesSelectedOptionsValues.includes(b.value);
      return isOptionBSelected - isOptionASelected;
    });
  }
}
